import { CatchBoundary, Link, useRouterState } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import {
  ArrowRightIcon,
  LockClosedIcon,
  ShieldCheckIcon,
  SparklesIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import { getButtonStyles } from '@sc/shared-ui';
import env from '../environment';
import { featurePages } from '../assets/data/features';
import { kbContent, type KbVariantType } from '../assets/data/kb';
import { LogoSlider } from './LogoSlider';
import { BottomCTA } from './BottomCTA';
import { FaqList } from './FaqList';
import { PricingPlans } from './PricingPlans';
import MainFooter from './MainFooter';
import { ResponsiveImage } from './ResponsiveImage';
import { CoverageDemo } from './CoverageDemo';
import ErrorScreen from './ErrorScreen';
import LaunchShape from '../assets/shapes/launch.svg?react';
import EmpowerShape from '../assets/shapes/empower.svg?react';
import EyeOverlayIcon from '../assets/shapes/eye-overlay.svg?react';
import ClockStopwatchIcon from '../assets/icons/clock-stopwatch.svg?react';

const primaryHref = env.appUrl + '/register';

/** SEO for a variant — the route's loader passes this to seoHead. */
export function kbSeo(variant: KbVariantType) {
  return kbContent[variant].seo;
}

interface Props {
  variant: KbVariantType;
}

export function KbPage({ variant }: Props) {
  const routerState = useRouterState();
  const content = kbContent[variant];
  const { accessControls, branding, features, hero, howItWorks, pricing } = content;

  const [stickyVisible, setStickyVisible] = useState(false);
  const heroSentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const node = heroSentinelRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => entry && setStickyVisible(!entry.isIntersecting),
      { rootMargin: '-64px 0px 0px 0px' }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const headline = (
    <>
      {hero.headline}{' '}
      <span className="text-primary-400">{hero.headlineHighlight}</span>
    </>
  );

  return (
    <CatchBoundary
      getResetKey={() => routerState.resolvedLocation?.state.key ?? ''}
      errorComponent={ErrorScreen}
    >
      <div className="bg-[#0C0C0F]">

        {/* ─── Sticky CTA (shown once the hero CTA has scrolled out of view) ── */}
        <div
          className={`fixed top-0 inset-x-0 z-60 h-14 bg-background-primary border-b border-grey-t-800 transition-transform duration-300 ${stickyVisible ? 'translate-y-0' : '-translate-y-full'
            }`}
          aria-hidden={!stickyVisible}
        >
          <div className="max-w-315 mx-auto px-5 h-full flex items-center justify-between gap-4">
            <img
              src="/lock-up.svg"
              alt="Stark Crawl"
              width="146"
              height="20"
              className="h-5 w-auto"
            />
            <a
              href={primaryHref}
              target="_blank"
              rel="noreferrer noopener"
              aria-label="Try Stark Crawl for free (opens in new tab)"
              {...getButtonStyles({
                type: 'primary',
                size: 'medium',
                additionalClassnames: 'gap-x-2 shrink-0',
              })}
            >
              {content.stickyCta}
              <ArrowRightIcon className="w-4 h-4" aria-hidden="true" />
            </a>
          </div>
        </div>

        <div className="relative overflow-x-hidden">
          <LaunchShape
            className="absolute left-30 top-40 -translate-x-1/2 w-160 opacity-4 pointer-events-none"
            aria-hidden="true"
          />
          <EmpowerShape
            className="absolute right-0 top-320 translate-x-1/2 w-216 opacity-4 pointer-events-none"
            aria-hidden="true"
          />

          <div className="px-5 pt-6 pb-2">
            <div className="max-w-315 mx-auto">
              <img
                src="/lock-up.svg"
                alt="Stark Crawl"
                width="146"
                height="20"
                className="h-5 w-auto"
              />
            </div>
          </div>

          {/* ─── Hero ───────────────────────────────────────────────────────── */}
          <section className="pt-10 lg:pt-15 px-5">
            <div className="max-w-220 mx-auto text-center flex flex-col items-center">
              <a
                href="#security"
                className="inline-flex items-center gap-2 bg-primary-1000 border border-primary-700 rounded-full px-4 py-2 shrink-0 bg-3"
              >
                <SparklesIcon
                  className="w-5 h-5 text-primary-300 shrink-0"
                  aria-hidden="true"
                />
                <span className="font-open text-xs leading-5 text-primary-300">
                  {hero.badge}
                </span>
              </a>
              <h1 className="font-grotesque text-[2.625rem] leading-[1.15] lg:text-[4.375rem] lg:leading-none font-medium text-white mb-8">
                {headline}
              </h1>
              <p className="text-lg leading-6.5 text-grey-300 max-w-180 mb-12">
                {hero.subhead}
              </p>
              <ul
                className="flex items-center gap-3 flex-wrap justify-center mb-8"
                role="list"
              >
                {hero.proofChips.map((chip, i) => (
                  <li
                    key={chip}
                    className="inline-flex items-center gap-2.5 border border-grey-t-700 rounded-full px-5 py-2.5"
                  >
                    {i === 0 && <ShieldCheckIcon
                      className="w-5 h-5 text-primary-400 shrink-0"
                      aria-hidden="true"
                    />}
                    {i === 1 && <UserIcon
                      className="w-5 h-5 text-primary-400 shrink-0"
                      aria-hidden="true"
                    />}
                    {i === 2 && <LockClosedIcon
                      className="w-5 h-5 text-primary-400 shrink-0"
                      aria-hidden="true"
                    />}
                    <span className="font-open text-sm leading-5 text-white">
                      {chip}
                    </span>
                  </li>
                ))}
              </ul>
              <div className="flex items-center gap-3 flex-wrap justify-center py-5">
                <a
                  href={primaryHref}
                  target="_blank"
                  rel="noreferrer noopener"
                  aria-label="Try Stark Crawl for free (opens in new tab)"
                  {...getButtonStyles({
                    type: 'primary',
                    size: 'xl',
                    additionalClassnames: 'gap-x-2',
                  })}
                >
                  {hero.primaryCta}
                  <ArrowRightIcon className="w-4.5 h-4.5" aria-hidden="true" />
                </a>
              </div>
            </div>
            {/* Marks "past the hero CTA" for the sticky bar — not a visible element. */}
            <div ref={heroSentinelRef} aria-hidden="true" />
            <div className="max-w-262 mx-auto mt-5">
              <div className="relative flex flex-col items-center rounded-lg overflow-hidden border-8 border-grey-t-700">
                <CoverageDemo />
              </div>
            </div>
          </section>

          {/* ─── Trusted By ───────────────────────────────────────────────────── */}
          <section className="py-15 px-5">
            <div className="max-w-315 mx-auto text-center">
              <p className="font-grotesque text-xl font-bold text-primary-400 uppercase leading-5 mb-5">
                {content.trustedBy.eyebrow}
              </p>
              <LogoSlider>
                <img src="/logos/able.svg" alt="Able" className="max-h-12.5" />
                <img src="/logos/aduro.svg" alt="Aduro" className="max-h-12.5" />
                <img
                  src="/logos/bsquared.webp"
                  alt="BSQuared"
                  className="max-h-12.5 opacity-30"
                />
                <img src="/logos/tfp.svg" alt="TFP" className="max-h-12.5" />
                <img
                  src="/logos/unibeez.svg"
                  alt="Unibeez"
                  className="max-h-12.5"
                />
              </LogoSlider>
            </div>
          </section>

          {/* ─── Features ─────────────────────────────────────────────────────── */}
          <section className="pt-25 pb-10 px-5">
            <div className="max-w-315 mx-auto">
              <p className="font-grotesque text-xl font-bold text-primary-400 uppercase leading-5 mb-3 text-center">
                {features.eyebrow}
              </p>
              <h2 className="font-grotesque text-4xl leading-[1.2] lg:text-[4rem] lg:leading-16 font-medium text-white mb-10 text-center">
                {features.heading}
              </h2>
              <p className="text-lg leading-6.5 mb-15 text-center text-grey-300">
                {features.intro}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {featurePages.map((f) => (
                  <Link
                    key={f.slug}
                    to={`/${f.slug}`}
                    className="bg-background-tertiary rounded-md backdrop-blur-[6.25rem] p-5 flex flex-col gap-5 flex-1 hover:bg-grey-t-900 transition-colors"
                  >
                    <div className="bg-primary-t-1000 inline-flex items-center justify-center p-4 rounded self-start shrink-0">
                      <f.Icon
                        className="w-6 h-6 text-primary-400"
                        aria-hidden="true"
                      />
                    </div>
                    <h3 className="font-grotesque text-[1.875rem] leading-9 font-semibold text-white">
                      {f.navTitle}
                    </h3>
                    <p className="text-base leading-6 text-grey-200">
                      {f.navDescription}
                    </p>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* ─── Private & Secure ─────────────────────────────────────────────── */}
          <section className="pt-25 pb-10 px-5" id="security">
            <div className="max-w-315 mx-auto flex flex-col lg:flex-row gap-10 items-center mb-25">
              <div className="flex flex-col gap-5 items-start w-full lg:w-115 order-2 lg:order-1">
                <p className="font-grotesque text-xl font-bold text-primary-400 uppercase leading-5">
                  {accessControls.eyebrow}
                </p>
                <h2 className="font-grotesque text-4xl leading-[1.2] lg:text-[4rem] lg:leading-16 font-medium text-white">
                  {accessControls.heading}
                </h2>
                <p className="text-base leading-6 text-grey-200">
                  {accessControls.body}
                </p>
                <ul className="flex flex-col gap-5 items-start" role="list">
                  {accessControls.bullets.map(({ Icon, text }) => (
                    <li key={text} className="flex gap-2 items-center">
                      <Icon
                        className="w-5 h-5 text-grey-400 shrink-0"
                        aria-hidden="true"
                      />
                      <p className="font-open text-base leading-6 text-white">
                        {text}
                      </p>
                    </li>
                  ))}
                </ul>
                <div className="flex gap-3 flex-wrap">
                  <Link
                    to="/privacy"
                    {...getButtonStyles({
                      type: 'basic',
                      size: 'large',
                    })}
                  >
                    {accessControls.linkLabel}
                  </Link>
                </div>
              </div>
              <div className="flex-1 w-full min-w-0 order-1 flex items-center justify-center min-h-96 py-10">
                <div className="relative flex flex-col items-center justify-center gap-8 w-full min-w-0">
                  <div className="relative flex items-center justify-center w-full max-w-125 min-w-0">
                    <EyeOverlayIcon
                      className="z-40 w-full h-auto pointer-events-none"
                      aria-hidden="true"
                    />
                    <ResponsiveImage
                      src="/images/security.png"
                      alt={accessControls.imageAlt}
                      className="absolute -top-[4.4%] z-10 w-4/5 aspect-square object-cover rounded-full"
                      sizes="400px"
                      loading="lazy"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* ─── Branding ─────────────────────────────────────────────────────── */}
            <div className="max-w-315 mx-auto flex flex-col lg:flex-row gap-10 items-center">
              <div className="flex-1 w-full min-w-0 order-1 flex items-center justify-center min-h-96 py-10">
                <div className="relative flex items-center justify-center w-125 max-w-full">
                  <EyeOverlayIcon
                    className="z-40 w-full h-auto pointer-events-none"
                    aria-hidden="true"
                  />
                  <ResponsiveImage
                    src="/images/theme.png"
                    alt={branding.imageAlt}
                    className="absolute -top-[4.4%] z-10 w-4/5 aspect-square object-cover rounded-full"
                    sizes="400px"
                    loading="lazy"
                  />
                </div>
              </div>
              <div className="flex flex-col gap-5 items-start w-full lg:w-115 order-2">
                <p className="font-grotesque text-xl font-bold text-primary-400 uppercase leading-5">
                  {branding.eyebrow}
                </p>
                <h2 className="font-grotesque text-4xl leading-[1.2] lg:text-[4rem] lg:leading-16 font-medium text-white">
                  {branding.heading}
                </h2>
                <p className="text-base leading-6 text-grey-200">{branding.body}</p>
                <ul className="flex flex-col gap-5 items-start" role="list">
                  {branding.bullets.map(({ Icon, text }) => (
                    <li key={text} className="flex gap-2 items-center">
                      <Icon
                        className="w-5 h-5 text-grey-400 shrink-0"
                        aria-hidden="true"
                      />
                      <p className="font-open text-base leading-6 text-white">
                        {text}
                      </p>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>

          {/* ─── How It Works (Steps) ─────────────────────────────────────────── */}
          <section className="pt-25 pb-10 px-5">
            <div className="max-w-315 mx-auto">
              <p className="font-grotesque text-xl font-bold text-primary-400 uppercase leading-5 mb-3 text-center">
                {howItWorks.eyebrow}
              </p>
              <h2 className="font-grotesque text-4xl leading-[1.2] lg:text-[4rem] lg:leading-16 font-medium text-white mb-15 text-center">
                {howItWorks.heading}
              </h2>
              <ul
                className="flex flex-col lg:flex-row gap-y-10 gap-x-10"
                role="list"
              >
                {howItWorks.steps.map(({ title, body, time }, index) => (
                  <li key={title} className="relative">
                    <h6
                      className="font-grotesque text-[10rem] leading-25 -top-4 opacity-10 absolute font-bold text-primary-t-600 select-none block"
                      aria-hidden="true"
                    >
                      {index + 1}
                    </h6>
                    <h3 className="font-grotesque mt-18 text-[1.875rem] leading-9 font-semibold text-white relative">
                      {title}
                    </h3>
                    <p className="text-base leading-6 text-grey-200 mt-5 mb-5">
                      {body}
                    </p>
                    <div className="inline-flex items-center gap-2 bg-primary-1000 border border-primary-700 rounded-full px-4 py-2">
                      <ClockStopwatchIcon
                        className="w-4 h-4 text-primary-300"
                        aria-hidden="true"
                      />
                      <span className="font-open text-xs leading-5 text-primary-300">
                        {time}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* ─── Pricing ──────────────────────────────────────────────────────── */}
          <section className="pt-25 pb-10 px-5">
            <div className="max-w-315 mx-auto">
              <p className="font-grotesque text-xl font-bold text-primary-400 uppercase leading-5 mb-3 text-center">
                {pricing.eyebrow}
              </p>
              <h2 className="font-grotesque text-4xl leading-[1.2] lg:text-[4rem] lg:leading-16 font-medium text-white mb-10 text-center">
                {pricing.heading}
              </h2>
              <p className="text-lg leading-6.5 mb-15 text-center text-grey-300 max-w-180 mx-auto">
                {pricing.intro}
              </p>
              <PricingPlans calculatorHref="/pricing#pricing-calculator" />
            </div>
          </section>

          {/* ─── FAQs ─────────────────────────────────────────────────────────── */}
          <section className="py-15 lg:py-20 px-5">
            <div className="max-w-315 mx-auto">
              <h2 className="font-grotesque text-4xl leading-[1.2] lg:text-[4rem] lg:leading-16 font-medium text-white mb-15 text-center">
                {content.faqsHeading}
              </h2>
              <FaqList faqs={content.faqs} />
            </div>
          </section>

          {/* ─── Closing CTA ──────────────────────────────────────────────────── */}
          <BottomCTA title={headline} body={content.closingBody} />

          <MainFooter />
        </div>
      </div>
    </CatchBoundary>
  );
}
